import { useEffect, useSyncExternalStore } from 'react';
import { DateTime } from 'luxon';

export type ThemeChoice = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';
export interface SunTimes {
  sunrise: string;
  sunset: string;
}

export const THEME_KEY = 'pacer-theme';
// The last resolved light/dark, cached so the blocking script in index.html can paint `auto`
// correctly on the next load instead of guessing.
const RESOLVED_KEY = 'pacer-theme-x';

// --- Shared module store (one source of truth for every consumer) ------------

let choice: ThemeChoice = readChoice();
let sun: SunTimes | null = null;
let zone = 'local';
let bootstrapped = false;
let resolved: ResolvedTheme = compute();
let snapshot = { choice, resolved };
const listeners = new Set<() => void>();

function readChoice(): ThemeChoice {
  try {
    const s = localStorage.getItem(THEME_KEY);
    if (s === 'light' || s === 'dark' || s === 'auto') return s;
    if (s === 'system') return 'auto'; // migrate the old "match system" option
  } catch {
    /* storage disabled */
  }
  return 'auto';
}

function prefersLight(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: light)').matches
    : false;
}

/** Auto = daylight is light, night is dark; falls back to the OS where there's no location. */
function compute(): ResolvedTheme {
  if (choice === 'light' || choice === 'dark') return choice;
  if (sun) {
    const now = DateTime.local().setZone(zone);
    const rise = DateTime.fromISO(sun.sunrise, { zone });
    const set = DateTime.fromISO(sun.sunset, { zone });
    if (rise.isValid && set.isValid) return now >= rise && now < set ? 'light' : 'dark';
  }
  // Before the first sun times arrive, trust the last resolution so the load never flashes.
  if (!bootstrapped) {
    try {
      const c = localStorage.getItem(RESOLVED_KEY);
      if (c === 'light' || c === 'dark') return c;
    } catch {
      /* ignore */
    }
  }
  return prefersLight() ? 'light' : 'dark';
}

function apply(theme: ResolvedTheme): void {
  document.documentElement.dataset.theme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'light' ? '#fbfbfc' : '#0b0c12');
  try {
    localStorage.setItem(RESOLVED_KEY, theme);
  } catch {
    /* ignore */
  }
}

function emit(): void {
  snapshot = { choice, resolved };
  for (const l of listeners) l();
}

function recompute(): void {
  const next = compute();
  if (next !== resolved) {
    resolved = next;
    apply(next);
    emit();
  }
}

export function setChoice(next: ThemeChoice): void {
  choice = next;
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    /* non-persistent is still usable this session */
  }
  resolved = compute();
  apply(resolved);
  emit();
}

/** The auto resolver's inputs: today's sun times + the household timezone. */
export function setSunContext(nextSun: SunTimes | null, nextZone: string): void {
  sun = nextSun;
  zone = nextZone || 'local';
  bootstrapped = true;
  recompute();
}

// --- Hooks ------------------------------------------------------------------

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useTheme(): {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
} {
  const snap = useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );
  return { choice: snap.choice, resolved: snap.resolved, setChoice };
}

/**
 * Mounted once at the app root. Feeds in sun times, flips the theme at the sunrise/sunset
 * boundary (a minute tick is plenty), and tracks the OS as the no-location fallback.
 */
export function useThemeDriver(nextSun: SunTimes | null, nextZone: string): void {
  useEffect(() => {
    setSunContext(nextSun ?? null, nextZone);
  }, [nextSun?.sunrise, nextSun?.sunset, nextZone]);

  useEffect(() => {
    const iv = setInterval(recompute, 60_000);
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: light)') : null;
    const onCh = (): void => recompute();
    mq?.addEventListener('change', onCh);
    return () => {
      clearInterval(iv);
      mq?.removeEventListener('change', onCh);
    };
  }, []);
}
